import type { DailyEntry, Profile, Reconciliation as ReconciliationType } from '@tally/lib/types';
import { mifflinBMR, stepKcal, NEAT_BASELINE_KCAL } from '@tally/lib/reconcile';

const KCAL_PER_KG = 7700;

const fmtKcal = (n: number | null) => (n == null ? '—' : Math.round(n).toLocaleString());
const fmtKg = (n: number | null, digits = 2) => (n == null ? '—' : Math.abs(n).toFixed(digits));
const mean = (xs: number[]) => (xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : null);

export function Reality({
  today,
  recent,
  reconciliation,
  profile,
  workoutKcalByDate,
}: {
  today: DailyEntry;
  recent: DailyEntry[];
  reconciliation: ReconciliationType | null;
  profile: Profile;
  workoutKcalByDate: Record<string, number>;
}) {
  // ---- Trusted inputs (intake + scale) ----
  const intakeDays = recent.slice(-7).filter((e) => e.calories_in != null);
  const dailyAvgIntake = mean(intakeDays.map((e) => e.calories_in as number));
  const weeklyIntake = dailyAvgIntake != null ? dailyAvgIntake * 7 : null;

  const weightPoints = recent.filter((e) => e.weight_kg != null).map((e) => e.weight_kg as number);
  const avg7 = mean(weightPoints.slice(-7));
  const avg7prev = weightPoints.length >= 14 ? mean(weightPoints.slice(-14, -7)) : null;
  const meanCompareDelta = avg7 != null && avg7prev != null ? avg7 - avg7prev : null;
  // Fallback when we don't have 14 weigh-ins yet: regression slope × 7.
  const weightSlopePerDay = (() => {
    const n = weightPoints.length;
    if (n < 3) return null;
    const xs = weightPoints.map((_, i) => i);
    const xMean = xs.reduce((a, b) => a + b, 0) / n;
    const yMean = weightPoints.reduce((a, b) => a + b, 0) / n;
    let num = 0, den = 0;
    for (let i = 0; i < n; i++) {
      num += (xs[i] - xMean) * (weightPoints[i] - yMean);
      den += (xs[i] - xMean) ** 2;
    }
    return den ? num / den : null;
  })();
  const weeklyWeightChange = meanCompareDelta ?? (weightSlopePerDay != null ? weightSlopePerDay * 7 : null);
  const actualLossKg = weeklyWeightChange != null ? -weeklyWeightChange : null;

  // ---- Estimated burn = BMR formula + measured activity (steps + WHOOP workouts) + NEAT baseline.
  //      Avoids WHOOP's daily total (which Tal doesn't trust) and only uses WHOOP's per-activity kcal,
  //      step count, and the well-established Mifflin BMR formula. ----
  const last7 = recent.slice(-7);
  const perDayEstimates = last7.map((e) => {
    if (e.weight_kg == null) return null;
    const bmr = mifflinBMR({
      weight_kg: e.weight_kg,
      height_cm: profile.height_cm,
      age: profile.age,
      sex: profile.sex,
    });
    const stepCalories = e.steps != null ? stepKcal(e.steps, e.weight_kg) : 0;
    const workoutCalories = workoutKcalByDate[e.date] ?? 0;
    return bmr + stepCalories + workoutCalories + NEAT_BASELINE_KCAL;
  }).filter((v): v is number => v != null);
  const dailyAvgEstBurn = mean(perDayEstimates);
  const weeklyEstBurn = dailyAvgEstBurn != null ? dailyAvgEstBurn * 7 : null;

  const workoutKcalLast7 = last7.reduce((s, e) => s + (workoutKcalByDate[e.date] ?? 0), 0);
  const stepKcalLast7 = last7.reduce((s, e) => s + (e.steps != null && e.weight_kg != null ? stepKcal(e.steps, e.weight_kg) : 0), 0);
  const haveAnySteps = last7.some((e) => e.steps != null);

  const estImpliedLossKg =
    weeklyIntake != null && weeklyEstBurn != null ? (weeklyEstBurn - weeklyIntake) / KCAL_PER_KG : null;

  // ---- Real TDEE (back-derived from scale + intake — the trusted method) ----
  const weeklyRealBurn =
    weeklyIntake != null && weeklyWeightChange != null ? weeklyIntake - weeklyWeightChange * KCAL_PER_KG : null;
  const tdee = weeklyRealBurn != null ? weeklyRealBurn / 7 : null;

  // ---- Deficits ----
  const todayIntake = today.calories_in;
  const dailyDeficit = tdee != null && todayIntake != null ? tdee - todayIntake : null;
  const weeklyDeficit = weeklyRealBurn != null && weeklyIntake != null ? weeklyRealBurn - weeklyIntake : null;
  const todayLossKg = dailyDeficit != null ? dailyDeficit / KCAL_PER_KG : null;

  // ---- Verdict: how much the formula+activity estimate diverges from reality ----
  const estGapKg =
    actualLossKg != null && estImpliedLossKg != null ? actualLossKg - estImpliedLossKg : null;
  const estGapKcalPerDay = estGapKg != null ? Math.round((estGapKg * KCAL_PER_KG) / 7) : null;

  // ---- Confidence: how many days of weight + intake do we have? ----
  const populatedDays = recent.filter((e) => e.weight_kg != null && e.calories_in != null).length;
  const stabilityNote =
    populatedDays >= 21 ? `${populatedDays}d of data — settled`
    : populatedDays >= 14 ? `${populatedDays}d of data — usable`
    : populatedDays >= 7 ? `${populatedDays}d of data — early signal, noisy`
    : `${populatedDays}d of data — not enough yet`;

  // ---- Tone helpers ----
  const lossVerb = (kg: number | null) => (kg == null ? '—' : kg >= 0.005 ? 'lost' : kg <= -0.005 ? 'gained' : 'stayed at');
  const lossNoun = (kg: number | null) => (kg == null ? 'change' : kg >= 0.005 ? 'weight loss' : kg <= -0.005 ? 'weight gain' : 'weight change');
  const toneFor = (kg: number | null) =>
    kg == null ? 'text-stone-300' : kg >= 0.005 ? 'text-emerald-400' : kg <= -0.005 ? 'text-amber-400' : 'text-stone-300';

  const intakeKg = weeklyIntake != null ? weeklyIntake / KCAL_PER_KG : null;
  const estBurnKg = weeklyEstBurn != null ? weeklyEstBurn / KCAL_PER_KG : null;

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* --- REALITY: comparison of WHOOP-implied vs actual --- */}
      <section className="border border-stone-700 bg-stone-900 p-6 space-y-4">
        <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500 font-mono">Reality</div>

        <Line>
          You ate <strong className="text-stone-100">{fmtKcal(weeklyIntake)}</strong> kcal{' '}
          {intakeKg != null && <span className="text-stone-500">({fmtKg(intakeKg)} kg)</span>} this week
          {dailyAvgIntake != null && <span className="text-stone-500"> — {fmtKcal(dailyAvgIntake)}/day avg</span>}
        </Line>

        <Line>
          <span className="text-stone-400">BMR + activity</span> says you burned{' '}
          <strong className="text-stone-100">{fmtKcal(weeklyEstBurn)}</strong> kcal{' '}
          {estBurnKg != null && <span className="text-stone-500">({fmtKg(estBurnKg)} kg)</span>} this week
          {dailyAvgEstBurn != null && <span className="text-stone-500"> — {fmtKcal(dailyAvgEstBurn)}/day avg</span>}
        </Line>
        <p className="text-[11px] text-stone-500 leading-relaxed -mt-2">
          BMR (Mifflin) + WHOOP workouts ({fmtKcal(workoutKcalLast7)} kcal/wk)
          {haveAnySteps && <> + steps ({fmtKcal(stepKcalLast7)} kcal/wk)</>} + {NEAT_BASELINE_KCAL} NEAT/day.
          {!haveAnySteps && ' No step data yet.'}
        </p>

        <div className="border-t border-stone-800 pt-4 space-y-2">
          <Line>
            <span className="text-stone-400">Estimate says you should have</span>{' '}
            <span className="text-violet-300">{lossVerb(estImpliedLossKg)}</span>{' '}
            <strong className="text-violet-300">{fmtKg(estImpliedLossKg)}</strong> kg
          </Line>
          <Line>
            <span className="text-stone-400">You actually</span>{' '}
            <span className="text-emerald-300">{lossVerb(actualLossKg)}</span>{' '}
            <strong className="text-emerald-300">{fmtKg(actualLossKg)}</strong> kg
          </Line>
          {estGapKcalPerDay != null && Math.abs(estGapKcalPerDay) >= 50 && (
            <p className="text-[11px] text-stone-500 leading-relaxed pt-1">
              {estGapKcalPerDay > 0
                ? `Lost more than expected — your intake log may be over-reported, OR you're more active than the activity log captures (~${estGapKcalPerDay} kcal/day).`
                : `Lost less than expected — likely water/glycogen retention, OR intake is under-reported (~${Math.abs(estGapKcalPerDay)} kcal/day).`}
            </p>
          )}
        </div>

        <div className="border-t border-stone-800 pt-4">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500 font-mono">Real TDEE</div>
              <div className="text-[10px] text-stone-600 font-mono mt-1">scale + fork · the truth</div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-light tabular-nums text-stone-100">
                {fmtKcal(tdee)} <span className="text-sm text-stone-500 font-mono">kcal/day</span>
              </div>
              <div className="text-[10px] text-stone-500 font-mono mt-1">{stabilityNote}</div>
            </div>
          </div>
        </div>

        {(weeklyIntake == null || weeklyWeightChange == null) && (
          <p className="text-[11px] text-amber-500/80 font-mono pt-2 border-t border-stone-800">
            {weeklyWeightChange == null
              ? 'Need 14 days of weigh-ins for a stable trend — keep logging.'
              : 'Need 7 days of intake to estimate weekly burn.'}
          </p>
        )}
      </section>

      {/* --- DEFICIT --- */}
      <section className="border border-stone-700 bg-stone-900 p-6 space-y-4">
        <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500 font-mono">Deficit</div>

        <Row label="Daily deficit" value={fmtKcal(dailyDeficit)} unit="kcal" tone={dailyDeficit == null ? 'default' : dailyDeficit > 0 ? 'good' : 'warn'} />
        <Row label="Weekly deficit" value={fmtKcal(weeklyDeficit)} unit="kcal" tone={weeklyDeficit == null ? 'default' : weeklyDeficit > 0 ? 'good' : 'warn'} />
        <Row
          label={`Today's ${lossNoun(todayLossKg)}`}
          value={fmtKg(todayLossKg)}
          unit="kg"
          tone={todayLossKg == null ? 'default' : todayLossKg >= 0.005 ? 'good' : 'warn'}
        />
        <Row
          label={`Weekly ${lossNoun(actualLossKg)}`}
          value={fmtKg(actualLossKg)}
          unit="kg"
          tone={actualLossKg == null ? 'default' : actualLossKg >= 0.005 ? 'good' : 'warn'}
        />
      </section>

      {/* --- Sanity check --- */}
      {reconciliation && (
        <details className="md:col-span-2 border border-stone-800 px-4 py-3 text-xs font-mono text-stone-500">
          <summary className="cursor-pointer hover:text-stone-300 select-none uppercase tracking-[0.2em] text-[10px]">
            formula sanity check
          </summary>
          <div className="mt-3 grid sm:grid-cols-2 gap-2 text-[11px]">
            {reconciliation.estimates.map((e, i) => (
              <div key={i} className="flex items-baseline justify-between border border-stone-800 px-3 py-2">
                <span className="text-stone-400">{e.label}</span>
                <span className="text-stone-200 tabular-nums">{e.value?.toLocaleString() ?? '—'}</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-stone-600 mt-3 leading-relaxed">
            For reference only. Mifflin/Katch are formulas that don't see your real intake or scale.
            WHOOP's burn is shown above as the cross-check. The Real TDEE in the Reality panel is back-derived
            from your scale and food log — that's the planning number.
          </p>
        </details>
      )}
    </div>
  );
}

function Line({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-stone-300 leading-relaxed">{children}</p>;
}

function Row({
  label,
  value,
  unit,
  tone,
}: {
  label: string;
  value: string;
  unit: string;
  tone: 'good' | 'warn' | 'default';
}) {
  const valueClass = tone === 'good' ? 'text-emerald-400' : tone === 'warn' ? 'text-amber-400' : 'text-stone-100';
  return (
    <div className="flex items-baseline justify-between border-b border-stone-800 pb-2 last:border-b-0 last:pb-0">
      <div className="text-sm text-stone-400 capitalize">{label}</div>
      <div className="flex items-baseline gap-1">
        <span className={`text-2xl font-light tabular-nums ${valueClass}`}>{value}</span>
        <span className="text-xs text-stone-500 font-mono">{unit}</span>
      </div>
    </div>
  );
}
